import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import { Badge } from "@/app/admin/_common/ui/badge";
import { Skeleton } from "@/app/admin/_common/ui/skeleton";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import CuoConfirmDialog from "@/app/admin/_common/CuoConfirmDialog";
import { formatUtcDate } from "@/app/_utils/dayjs";
import type { PromoCode } from "@/app/_services/PromoService";
import { cn } from "@/app/_utils/cn";
import { MoreHorizontal, Pencil, Trash2, Tag, Power, PowerOff } from "lucide-react";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";

/** Show chips rendered before the rest collapse into a "+N more" label. */
const MAX_SHOW_CHIPS = 2;

export default function PromoList({
  promos,
  showMap,
  isShowMapLoading,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  promos: PromoCode[];
  showMap: Record<string, string>;
  isShowMapLoading?: boolean;
  onEdit: (promo: PromoCode) => void;
  onToggleActive: (promo: PromoCode) => void;
  onDelete: (promo: PromoCode) => void;
}) {
  const [pendingDelete, setPendingDelete] = useState<PromoCode | null>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const statusPill = (promo: PromoCode) => (
    <Badge
      variant="outline"
      className={cn(
        "gap-1.5 rounded-[7px] font-normal",
        promo.active
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-slate-200 bg-slate-50 text-slate-600"
      )}
    >
      <span
        className={cn(
          "size-1.5 flex-none rounded-full",
          promo.active ? "bg-emerald-500" : "bg-slate-400"
        )}
      />
      {promo.active ? "Active" : "Inactive"}
    </Badge>
  );

  const showsCell = (promo: PromoCode) => {
    if (isShowMapLoading && promo.shows.length > 0) {
      return <Skeleton className="h-3 w-32" />;
    }
    if (promo.shows.length === 0) {
      return <span className="text-[12.5px] text-muted-foreground">All shows</span>;
    }
    const visible = promo.shows.slice(0, MAX_SHOW_CHIPS);
    const remaining = promo.shows.length - visible.length;
    return (
      <div className="flex min-w-0 items-center gap-1.5 text-[12.5px] text-muted-foreground">
        <span className="truncate">{visible.map((id) => showMap[id] ?? "Show").join(", ")}</span>
        {remaining > 0 && <span className="flex-none">+{remaining} more</span>}
      </div>
    );
  };

  const actionsMenu = (promo: PromoCode) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ml-auto flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
        >
          <MoreHorizontal className="size-4 shrink-0" size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onEdit(promo)}>
          <Pencil className="mr-2 size-4" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onToggleActive(promo)}>
          {promo.active ? <PowerOff className="mr-2 size-4" /> : <Power className="mr-2 size-4" />}
          {promo.active ? "Deactivate" : "Activate"}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-red-600 focus:bg-red-50 focus:text-red-700"
          onClick={() => setPendingDelete(promo)}
        >
          <Trash2 className="mr-2 size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const confirmDialog = (
    <CuoConfirmDialog
      open={pendingDelete !== null}
      onClose={() => setPendingDelete(null)}
      title="Delete promo code"
      message={`This action cannot be undone. Are you sure you want to delete ${
        pendingDelete?.code ?? "this promo code"
      }?`}
      onConfirm={async () => {
        if (pendingDelete) await onDelete(pendingDelete);
      }}
      icon={<Trash2 />}
    />
  );

  if (isMobile) {
    return (
      <div className="flex flex-col bg-card">
        {promos.length === 0 ? (
          <div className="flex min-h-[400px] flex-col">
            <CuoEmpty icon={Tag} text="No promo codes" />
          </div>
        ) : (
          <div className="flex flex-col">
            {promos.map((promo) => (
              <div
                key={promo.id}
                className="flex cursor-pointer items-center gap-2.5 border-b border-border px-4 py-2.5 last:border-0"
                onClick={() => navigate(`/admin/promos/${promo.id}`)}
              >
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-mono text-[14px] font-medium text-foreground">
                      {promo.code}
                    </span>
                    <span className="flex-none text-[12.5px] text-muted-foreground">
                      {promo.discount_percent}% off
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusPill(promo)}
                    <span className="truncate text-[12.5px] text-muted-foreground">
                      {promo.shows.length === 0
                        ? "All shows"
                        : `${promo.shows.length} ${promo.shows.length === 1 ? "show" : "shows"}`}
                    </span>
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>{actionsMenu(promo)}</div>
              </div>
            ))}
          </div>
        )}

        {confirmDialog}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className={cn("min-w-[900px] table-fixed", promos.length === 0 && "h-full")}
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 w-1/5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Code
            </TableHead>
            <TableHead className="h-8 w-[12%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Discount
            </TableHead>
            <TableHead className="h-8 w-[33%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Shows
            </TableHead>
            <TableHead className="h-8 w-[15%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Status
            </TableHead>
            <TableHead className="h-8 w-[15%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Created
            </TableHead>
            <TableHead className="h-8 w-12 pr-6 text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {promos.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={6} className="h-full p-0">
                <CuoEmpty icon={Tag} text="No promo codes" />
              </TableCell>
            </TableRow>
          ) : (
            promos.map((promo) => (
              <TableRow
                key={promo.id}
                className="group cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/admin/promos/${promo.id}`)}
              >
                <TableCell className="py-1.5">
                  <span className="truncate font-mono text-[13px] font-medium text-foreground group-hover:underline">
                    {promo.code}
                  </span>
                </TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-[12.5px] text-muted-foreground">
                  {promo.discount_percent}% off
                </TableCell>
                <TableCell className="py-1.5">{showsCell(promo)}</TableCell>
                <TableCell className="py-1.5">{statusPill(promo)}</TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-[12.5px] text-muted-foreground">
                  {formatUtcDate(promo.created_at)}
                </TableCell>
                <TableCell
                  className="py-1.5 pr-6 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  {actionsMenu(promo)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {confirmDialog}
    </div>
  );
}
